import { getBookingReportFiles, getBookingReportPartKey } from "./BookingReportChecklistUtils";

export const isEmptyBookingAhi = (value) =>
  value === undefined || value === null || (typeof value === "string" && value.trim() === "");

const text = (value) => String(value == null ? "" : value).trim();
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const containsBookingOrder = (value, order) => {
  if (!text(order)) return false;
  const pattern = text(order).split(/[-_\s]+/).map(escapeRegExp).join("[-_\\s]+");
  return new RegExp(`(^|[^a-z0-9])${pattern}(?=$|[^a-z0-9])`, "i").test(text(value));
};

// Patient report folders contain several studies. Never assume their latest
// report belongs to this booking, and never use a trial or medical certificate.
export const getAhiReportCandidates = (reports, booking, path) => {
  const props = booking.extendedProps || {};
  const files = [];
  reports.forEach((report) => {
    if (report.hn && text(report.hn) !== text(props.hn)) return;
    if (report.bookingUploadBookingPath && report.bookingUploadBookingPath !== path) return;
    getBookingReportFiles(report).forEach((fileName) => {
      if (getBookingReportPartKey(fileName) !== "sleepStudyReport") return;
      files.push({
        id: report.id, hn: text(props.hn), fileName,
        matched: report.bookingUploadBookingPath === path || containsBookingOrder(fileName, props.order),
      });
    });
  });
  const matched = files.filter((file) => file.matched);
  return matched.length ? matched : files;
};

// This parser is deliberately stricter than the interactive certificate reader:
// bulk writes cannot ask an operator to choose between competing clinical values.
export const extractBookingAhi = (rawText) => {
  const source = text(rawText).replace(/\u00a0/g, " ").replace(/[–—]/g, "-");
  // PDF text can wrap the full label between "apnea-" and "hypopnea".
  const label = "(?:apn(?:ea|oea)[\\s/-]+hypopn(?:ea|oea)\\s+index(?:\\s*\\(AHI\\))?|\\bAHI\\b)";
  const pattern = new RegExp(`${label}(?:\\s*(?:index|value))?(?:\\s*\\((?:events?\\s*[/]\\s*(?:h|hr|hour)|[/]h|#\\/h)\\))?\\s*(?::|=|was|is|of)?\\s*(\\d+(?:[.,]\\d+)?)(?![\\da-z]|[.,]\\d)`, "gi");
  const candidates = [];
  let match;
  while ((match = pattern.exec(source))) {
    const before = source.slice(0, match.index).split(/\n|[.;](?!\d)/).pop();
    const after = source.slice(pattern.lastIndex).split(/\n|[.;](?!\d)/)[0];
    const context = `${before} ${after}`;
    if (/\b(?:non[- ]?rem|nrem|rem|supine|prone|lateral|central|obstructive|residual|cpap|pap|titration|normal|mild|moderate|severe|reference)\b/i.test(context)) continue;
    // A multi-column row is not a single total; ranges and thresholds are not results.
    if (/^\s*(?:-|to\b|\d|%)/i.test(after) || /[<>≤≥]/.test(before.slice(-4))) continue;
    const value = Number(match[1].replace(",", "."));
    if (!Number.isFinite(value) || value < 0 || value > 300) continue;
    const priority = /\b(?:diagnostic|baseline)\b/i.test(before) ? 2 : /\b(?:total|overall)\b/i.test(before) ? 1 : 0;
    candidates.push({ value, priority });
  }
  if (!candidates.length) throw new Error("No unambiguous overall/diagnostic AHI found (image-only, unsupported layout, or missing AHI).");
  const priority = Math.max(...candidates.map((candidate) => candidate.priority));
  const values = Array.from(new Set(candidates.filter((candidate) => candidate.priority === priority).map((candidate) => candidate.value)));
  if (values.length !== 1) throw new Error(`Conflicting AHI values in report: ${values.join(", ")}.`);
  return String(values[0]);
};

export const updateEmptyBookingAhi = async ({ runTransaction, db, bookingRef, original, ahi }) => {
  // Validate again at the write boundary; never coerce labels, objects, empty
  // strings, or arbitrary report text into a booking value.
  if (!["string", "number"].includes(typeof ahi) || !/^\d+(?:\.\d+)?$/.test(text(ahi)) || !Number.isFinite(Number(ahi)) || Number(ahi) > 300) {
    throw new Error("Invalid AHI: expected only a numeric report value between 0 and 300.");
  }
  const numericAhi = String(Number(ahi));
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(bookingRef);
    if (!snapshot.exists()) throw new Error("Booking no longer exists.");
    const current = snapshot.data().extendedProps || {};
    if (!isEmptyBookingAhi(current.ahi)) return { status: "skipped", ahi: current.ahi };
    if (["hn", "order", "node", "latestSleepStudyDate"].some((key) => text(current[key]) !== text(original[key]))) {
      throw new Error("Booking identity/study changed while reading its report. Please retry.");
    }
    const updatedBy = "Auto refill AHI";
    const updatedAt = new Date();
    transaction.update(bookingRef, {
      "extendedProps.ahi": numericAhi,
      "extendedProps.updatedBy": updatedBy,
      "extendedProps.updatedAt": updatedAt,
    });
    return { status: "updated", ahi: numericAhi, updatedBy, updatedAt };
  });
};

export const runBookingAhiRefill = async ({ cases, readAhi, saveAhi, onProgress, onSaved, beforeCase = () => {} }) => {
  const result = { total: cases.length, processed: 0, updated: 0, skipped: 0, failed: 0, errors: [] };
  onProgress({ ...result });
  for (const item of cases) {
    beforeCase();
    let stage = "read_report";
    try {
      const ahi = await readAhi(item);
      stage = "update_ahi";
      const saved = await saveAhi(item, ahi);
      result[saved.status === "updated" ? "updated" : "skipped"] += 1;
      if (saved.status === "skipped") result.errors.push({ path: item.ref.path, ...item.data.extendedProps, stage, error: "Skipped: AHI was filled by another update." });
      onSaved(item, saved.ahi, saved);
    } catch (error) {
      result.failed += 1;
      result.errors.push({ path: item.ref.path, ...item.data.extendedProps, stage, error: (error && error.message) || String(error) });
    }
    result.processed += 1;
    onProgress({ ...result, errors: [...result.errors] });
  }
  return result;
};

export const ahiErrorLogCsv = (errors) => {
  const columns = ["path", "order", "hn", "node", "stage", "error"];
  const cell = (value) => {
    const safe = text(value).replace(/https?:\/\/\S+/gi, "[URL omitted]");
    return `"${(/^[=+@-]/.test(safe) ? "'" : "") + safe.replace(/"/g, '""')}"`;
  };
  return "\uFEFF" + [columns.join(","), ...errors.map((error) => columns.map((key) => cell(error[key])).join(","))].join("\r\n");
};
